import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

const HowToUseADUAirbnbRental = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-airbnb-rental" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-background to-muted py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-8 text-[hsl(var(--brand-primary))]">
            How to Use Your ADU as a Short-Term Airbnb Rental
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[hsl(var(--brand-primary))]">
            Turn Your Backyard Into a Five-Star Stay
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-12">
            Short-term rentals are in high demand—and your ADU could become the next highly rated stay in your city. With the right layout and amenities, we'll help you design a unit guests love—and that generates consistent income for you.
          </p>
          
          {/* Top CTA Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Airbnb Photos Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/5ead1609-cc1f-4591-8777-503d1d22c1b7.png" 
                  alt="Warm modern interior with hotel-style finishes"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/c51f23eb-430e-42b6-afbf-c4e54d033948.png" 
                  alt="Cozy bedroom with stylish design"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/07dfc299-225b-4d3c-b4c3-7ae077c4b1ee.png" 
                  alt="Private exterior access with patio setup"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Airbnb-Ready Design Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-[hsl(var(--brand-primary))]">
            Airbnb-Ready Design Starts with Smart Planning
          </h2>
          <p className="text-lg text-muted-foreground text-center mb-12">
            When we build an ADU with Airbnb in mind, we consider:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Independent Entrances and Clear Separation</h3>
                <p className="text-muted-foreground">
                  Complete privacy for guests while maintaining your family's comfort and security in the main house.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Durable Finishes for Easy Cleaning</h3>
                <p className="text-muted-foreground">
                  High-traffic materials and surfaces designed for quick turnovers and consistent guest satisfaction.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Hotel-Style Touches</h3>
                <p className="text-muted-foreground">
                  Smart locks, USB outlets, compact kitchenettes, and thoughtful amenities that guests expect and love.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Clear Zones for Living</h3>
                <p className="text-muted-foreground">
                  Defined areas for sleeping, relaxing, and dining that maximize comfort in compact spaces.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What Hosts Love Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-[hsl(var(--brand-primary))]">
            What Hosts Love About ADUs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Set Your Own Schedule and Pricing</h3>
                <p className="text-muted-foreground">
                  Complete control over availability, rates, and house rules to fit your lifestyle and income goals.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">No Commute to Manage Your Listing</h3>
                <p className="text-muted-foreground">
                  Handle check-ins, maintenance, and guest communication without leaving your property.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Meet Amazing Guests from Around the World</h3>
                <p className="text-muted-foreground">
                  Connect with travelers and create memorable experiences while building your hosting reputation.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Build Wealth Through Passive Income</h3>
                <p className="text-muted-foreground">
                  Generate substantial monthly revenue that can pay off your ADU investment and create long-term wealth.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>💡 Tip:</strong> We can help ensure your design meets short-term rental codes in your city or county.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maximize ROI Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8 text-[hsl(var(--brand-primary))]">
            Maximize Your Nightly ROI
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-12">
            We'll help you run the numbers. In many Bay Area neighborhoods, hosts are earning $150–$250 per night for well-designed 1-bedroom ADUs. Some earn $3,500–$5,000+ per month depending on seasonality and guest volume.
          </p>
          
          <div className="max-w-3xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-6">Long-term Rent vs Short-term ROI (Average)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="bg-gray-100 p-6 rounded-lg">
                      <h4 className="font-semibold text-lg mb-2">Traditional Long-Term Rent</h4>
                      <p className="text-3xl font-bold text-gray-600 mb-2">$2,500</p>
                      <p className="text-sm text-muted-foreground">Average monthly rent</p>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="bg-[hsl(var(--brand-primary))]/10 p-6 rounded-lg">
                      <h4 className="font-semibold text-lg mb-2">Short-Term Airbnb</h4>
                      <p className="text-3xl font-bold text-[hsl(var(--brand-primary))] mb-2">$4,200</p>
                      <p className="text-sm text-muted-foreground">Average monthly earnings</p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  *Earnings vary by location, seasonality, and occupancy rates. Based on Bay Area market data.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom Call-to-Action Section */}
      <section className="py-16 px-4 bg-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary-foreground))]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Ready to Start Your Airbnb Success Story?</h2>
          <p className="text-lg mb-12 opacity-90">
            Whether it's a weekend guest or a 30-day visitor, your backyard ADU can become a premium stay with unbeatable ROI. Let UDA help you build a guest-ready rental that pays for itself.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 w-full"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/our-work" 
                className="w-full"
              >
                🏡 Our Portfolio ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/faq" 
                className="w-full"
              >
                ❓ Our FAQ ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
      <Footer />
      <FloatingADUWizard />
    </div>
  );
};

export default HowToUseADUAirbnbRental;